import { useEffect, useRef, type ReactNode } from "react";
import { Button } from "./Button";

// Tabbable elements inside the dialog panel (focus-trap boundary). No visibility filtering:
// jsdom reports offsetParent as null for everything, and hidden controls inside an open modal
// are rare enough that trapping to the raw match list is the safer cross-environment contract.
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Keep critical decision dialogs open until their own action resolves. */
  dismissible?: boolean;
  /** Optional id of concise supporting text announced with the dialog title. */
  ariaDescribedBy?: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  dismissible = true,
  ariaDescribedBy,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Ref-forwarded close handler so the focus/keyboard effect keys on `open` alone — an inline
  // onClose prop must not tear down the trap (and restore focus) on every parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  // Capture the restore target at the closed→open RENDER transition — the effect below runs
  // after commit, by which point an [autofocus] child has already taken focus and
  // document.activeElement would wrongly point inside the dialog.
  const wasOpen = useRef(false);
  const restoreRef = useRef<HTMLElement | null>(null);
  if (open && !wasOpen.current) {
    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasOpen.current = open;

  useEffect(() => {
    if (!open) return;
    const restoreTo = restoreRef.current;

    // Initial focus: respect a child that autofocused itself during mount (e.g. the command
    // palette's search input); otherwise the first focusable control; else the panel itself.
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      const auto = panel.querySelector<HTMLElement>("[autofocus]");
      (auto ?? panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel).focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissibleRef.current) {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const trap = panelRef.current;
      if (!trap) return;
      const els = Array.from(trap.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (els.length === 0) {
        e.preventDefault();
        trap.focus();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && trap.contains(active);
      if (e.shiftKey) {
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      // Focus returns to the element that opened the dialog (if it is still in the document).
      if (restoreTo && restoreTo.isConnected) restoreTo.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby={ariaDescribedBy}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={dismissible ? onClose : undefined}
    >
      <div
        className="bg-[var(--surface-sunken)] rounded-[var(--radius-sm)] shadow-[var(--shadow-lg)] max-w-lg w-full border border-[var(--color-border-default)]"
        onClick={(e) => e.stopPropagation()}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="px-6 py-4 border-b border-[var(--color-border-subtle)] flex items-center justify-between">
          <h2 id="modal-title" className="text-lg font-semibold text-[var(--color-text-primary)]">
            {title}
          </h2>
          {dismissible && (
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close dialog">
              ×
            </Button>
          )}
        </div>
        <div className="px-6 py-4 text-sm text-[var(--color-text-primary)]">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-[var(--color-border-subtle)] flex justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
